/**
 * Route guard for parent-facing pages.
 *
 * Subscribes to onAuthStateChanged and:
 *  - while Firebase is still resolving the initial token: render nothing
 *    (a flash to /auth would be visually jarring on a hard refresh)
 *  - once resolved with no user: redirect to /auth
 *  - once resolved with a user: render the protected children
 *
 * Apollo's `useAuthErrorHandler` already pushes 401 responses back to
 * /auth, so this guard exists for the unauthenticated-but-no-API-call
 * case (typing the URL, refreshing on a stale tab).
 */
import React, {useEffect, useState} from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {Box, CircularProgress} from '@mui/material';
import {onAuthStateChanged, User} from 'firebase/auth';
import {auth} from '../../firebase';

interface Props {
  children: React.ReactNode;
}

export function RequireAuth({children}: Props): React.JSX.Element {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u);
      setResolved(true);
    });
    return () => unsub();
  }, []);

  if (!resolved) {
    // Brief loading splash while Firebase rehydrates the persisted
    // session — typically resolves in a few hundred ms.
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace state={{from: location.pathname}} />;
  }

  return <>{children}</>;
}
